import { format } from 'date-fns'
import { FC, Fragment, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { CalendarMonth, EditOutlined, ExpandMore, OpenInNew, ReportProblemOutlined, Search } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Skeleton,
  Stack,
  TextField,
  Theme,
  Typography,
  alpha,
} from '@mui/material'

import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { GameIcon } from '../../../../../features/game'
import { getUTCDate } from '../../../../../helpers/date'
import { borderRadiusDefault } from '../../../../../themes/theme'
import { languageServiceNameResolver } from '../../../../components/BlueprintFamilySearch/blueprintFamilyGrouping'
import {
  OddCaseEvent,
  OddCaseEventHistory,
  OddCaseHistoryByBlueprint,
  OddCaseTypeGroup,
  filterOddCaseGroups,
  groupLastSeen,
  historyOf,
  splitAcceptedOddCases,
  startsGameRun,
} from '../../helpers/oddCaseEvents'
import { BlueprintFamilyUsage } from '../../hooks/useBlueprintFamilyUsage'
import { useBlueprintOddCaseEvents } from '../../hooks/useBlueprintOddCaseEvents'
import { customerBlueprintUrl, customerCalendarUrl } from '../CustomerBlueprintLink/customerLiveEventsUrls'

// The table's rules, bands, hovers, muted text and icon buttons, as tints of the theme palette.
const ruleColor = (theme: Theme) => theme.palette.background.default
const eventRowBorder = (theme: Theme) => alpha(theme.palette.background.default, 0.8)
const filterBorder = (theme: Theme) => alpha(theme.palette.text.primary, 0.18)
const groupBandBackground = (theme: Theme) => alpha(theme.palette.background.default, 0.4)
const groupBandHoverBackground = (theme: Theme) => alpha(theme.palette.background.default, 0.7)
const eventRowHoverBackground = (theme: Theme) => alpha(theme.palette.background.default, 0.25)
const summaryChipBackground = (theme: Theme) => alpha(theme.palette.warning.main, 0.1)
const mutedCellText = (theme: Theme) => alpha(theme.palette.text.primary, 0.75)
const chevronColor = (theme: Theme) => alpha(theme.palette.text.primary, 0.65)
const quietIconColor = (theme: Theme) => alpha(theme.palette.text.primary, 0.5)
const iconButtonHoverBackground = (theme: Theme) => alpha(theme.palette.primary.main, 0.1)

// Rows run edge to edge and hold their text in line with the card title themselves, so a hover or a
// rule reaches both edges of the card.
const rowPaddingX = '26px'
// The fraction columns may shrink below their content, which is what lets the names ellipsise and
// keeps every row on the same columns.
const tableColumns = 'minmax(0, 1.3fr) minmax(0, 2fr) minmax(0, 1.2fr) 120px 64px 106px'
const tableColumnGap = '16px'

type BlueprintFamilyOddCasesCardProps = {
  usage: BlueprintFamilyUsage
}

/**
 * On a Blueprint Family's overview, the event types tagged on the blueprint that it does not
 * accept, and the events behind each one. The analyst works the list down to zero by retagging the
 * events, or by widening the blueprint's accepted types.
 *
 * One table, with a band per event type. The types the blueprint does accept are not listed: there
 * is nothing to fix in them.
 *
 * Groups start closed to keep the card short, and open at once since the blueprint carries the
 * events. Opening the first one also reads how often each event ran, from the evolution of the few
 * game blueprints involved.
 */
export const BlueprintFamilyOddCasesCard: FC<BlueprintFamilyOddCasesCardProps> = ({ usage }) => {
  const [historyRequested, setHistoryRequested] = useState(false)
  const oddCases = useBlueprintOddCaseEvents(usage, { historyEnabled: historyRequested })

  return (
    <BlueprintFamilyOddCasesList
      groups={oddCases.groups}
      histories={oddCases.histories}
      hasAcceptedTypes={(usage.blueprintFamily.eventTypes?.length || 0) > 0}
      blueprintFamilyId={usage.blueprintFamily.id}
      checked={oddCases.checked}
      failed={oddCases.failed}
      total={oddCases.total}
      historyRequested={historyRequested}
      isHistoryComplete={oddCases.isHistoryComplete}
      onHistoryNeeded={() => setHistoryRequested(true)}
    />
  )
}

type BlueprintFamilyOddCasesListProps = {
  groups: OddCaseTypeGroup[]
  histories: OddCaseHistoryByBlueprint
  /** Without a declared accepted type every type its events use comes back as not accepted. */
  hasAcceptedTypes: boolean
  blueprintFamilyId: string
  /** Game blueprint histories read so far, failures included, out of total. */
  checked: number
  failed: number
  total: number
  historyRequested: boolean
  isHistoryComplete: boolean
  /** Called the first time events are on show: opening a group, or filtering. */
  onHistoryNeeded: () => void
}

export const BlueprintFamilyOddCasesList: FC<BlueprintFamilyOddCasesListProps> = ({
  groups,
  histories,
  hasAcceptedTypes,
  blueprintFamilyId,
  checked,
  failed,
  total,
  historyRequested,
  isHistoryComplete,
  onHistoryNeeded,
}) => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const [openGroupKeys, setOpenGroupKeys] = useState<string[]>([])
  const [showAccepted, setShowAccepted] = useState(false)

  // Events an analyst accepted as exceptions are done with, so they stay out of the way unless asked
  // for.
  const { toReview, accepted } = useMemo(() => splitAcceptedOddCases(groups), [groups])
  const acceptedEventCount = accepted.reduce((count, group) => count + group.events.length, 0)

  // Without a declared accepted type the events say nothing about being wrong, so nothing is counted
  // as a mismatch until the blueprint has been curated. Its types are still listed so the analyst can
  // see them.
  const flaggedEventCount = hasAcceptedTypes ? toReview.reduce((count, group) => count + group.events.length, 0) : 0

  const filteredToReview = useMemo(() => filterOddCaseGroups(toReview, filter), [toReview, filter])
  const filteredAccepted = useMemo(() => (showAccepted ? filterOddCaseGroups(accepted, filter) : []), [accepted, filter, showAccepted])
  const isFiltering = filter.trim().length > 0
  const isLoadingHistory = historyRequested && !isHistoryComplete

  // Groups start closed, and a filter opens whatever still matches.
  const isExpanded = (groupKey: string) => isFiltering || openGroupKeys.includes(groupKey)

  const toggleGroup = (groupKey: string) => {
    setOpenGroupKeys((current) => (current.includes(groupKey) ? current.filter((key) => key !== groupKey) : [...current, groupKey]))
    if (!historyRequested) onHistoryNeeded()
  }

  const renderGroups = (shown: OddCaseTypeGroup[], all: OddCaseTypeGroup[], section: string) =>
    shown.map((group) => {
      const groupKey = `${section}-${group.typeId}`
      const totalEventCount = all.find((candidate) => candidate.typeId === group.typeId)?.events.length ?? group.events.length
      const expanded = isExpanded(groupKey)
      return (
        <Fragment key={groupKey}>
          <OddCaseGroupRow
            group={group}
            lastSeenTimestamp={groupLastSeen(group, histories)}
            totalEventCount={totalEventCount}
            isFiltering={isFiltering}
            expanded={expanded}
            onToggle={() => toggleGroup(groupKey)}
          />
          {expanded &&
            group.events.map((event, index) => (
              <OddCaseEventRow
                key={`${event.blueprintId}-${event.eventId}`}
                event={event}
                showGame={startsGameRun(group.events, index)}
                history={historyOf(event, histories)}
                isHistoryPending={isLoadingHistory}
              />
            ))}
        </Fragment>
      )
    })

  const hasRows = filteredToReview.length > 0 || filteredAccepted.length > 0
  const emptyMessage = isFiltering
    ? t('internal-live-events:odd_cases_no_matches')
    : acceptedEventCount > 0
    ? t('internal-live-events:odd_cases_none_to_review')
    : t('internal-live-events:odd_cases_none_flagged')

  const handleFilterChange = (value: string) => {
    setFilter(value)
    if (value.trim().length > 0 && !historyRequested) onHistoryNeeded()
  }

  return (
    <Card sx={{ marginTop: 3 }}>
      <CardHeader
        disableTypography
        title={
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <ReportProblemOutlined color={flaggedEventCount > 0 ? 'warning' : 'disabled'} sx={{ mt: '1px' }} />
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h5" component="span">
                  {t('internal-live-events:odd_cases')}
                </Typography>
                {flaggedEventCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: '2px',
                      borderRadius: '10px',
                      backgroundColor: summaryChipBackground,
                      color: 'warning.main',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('internal-live-events:event_count', { count: flaggedEventCount })} ·{' '}
                    {t('internal-live-events:odd_cases_types_count', { count: toReview.length })}
                  </Box>
                )}
              </Stack>
              <Typography sx={{ mt: '4px', fontSize: '13px' }} color="text.secondary">
                {t('internal-live-events:odd_cases_helper')}
              </Typography>

              {isLoadingHistory && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <Skeleton variant="circular" width={14} height={14} />
                  <Typography sx={{ fontSize: '13px' }} color="text.secondary">
                    {total > 1 ? t('internal-live-events:odd_cases_checking', { checked, total }) : t('internal-live-events:odd_cases_loading_events')}
                  </Typography>
                </Stack>
              )}

              {isHistoryComplete && failed > 0 && (
                <Typography sx={{ mt: 1, fontSize: '13px' }} color="text.secondary">
                  {t('internal-live-events:odd_cases_check_failed', { count: failed })}
                </Typography>
              )}

              {groups.length > 0 && !hasAcceptedTypes && (
                <Typography sx={{ mt: 1, fontSize: '13px' }} color="text.secondary">
                  {t('internal-live-events:odd_cases_no_accepted_types')}{' '}
                  <MuiLink component={Link} to={`/internal/live-events-tracking/blueprint-families/edit/${blueprintFamilyId}`}>
                    {t('internal-live-events:edit_blueprint_family')}
                  </MuiLink>
                </Typography>
              )}
            </Box>
          </Stack>
        }
        action={
          groups.length > 0 && (
            <TextField
              value={filter}
              onChange={(event) => handleFilterChange(event.target.value)}
              size="small"
              placeholder={t('internal-live-events:odd_cases_filter_placeholder')}
              inputProps={{ 'aria-label': t('internal-live-events:odd_cases_filter_placeholder') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 15, color: quietIconColor }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': { height: 36, borderRadius: borderRadiusDefault, paddingLeft: 0 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: filterBorder },
                '& .MuiInputAdornment-root': { position: 'absolute', left: '13px', margin: 0 },
                '& .MuiOutlinedInput-input': { paddingLeft: '36px', fontSize: '0.8125rem' },
              }}
            />
          )
        }
        sx={{ p: '22px 26px 18px', alignItems: 'flex-start', '& .MuiCardHeader-action': { alignSelf: 'flex-start', margin: 0, pl: 2 } }}
      />
      <CardContent sx={{ p: 0, '&:last-child': { paddingBottom: 0 } }}>
        <Box sx={{ '& > :last-child': { borderBottom: 0 } }}>
          {hasRows && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                columnGap: tableColumnGap,
                px: rowPaddingX,
                py: '10px',
                fontSize: '10.5px',
                fontWeight: 'bold',
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                borderTop: (theme) => `1px solid ${ruleColor(theme)}`,
                borderBottom: (theme) => `1px solid ${ruleColor(theme)}`,
              }}
            >
              <span>{t('internal-live-events:odd_cases_column_game')}</span>
              <span>{t('internal-live-events:odd_cases_column_event')}</span>
              <span>{t('internal-live-events:odd_cases_column_game_blueprint')}</span>
              <span>{t('internal-live-events:odd_cases_column_last_seen')}</span>
              <Box component="span" sx={{ textAlign: 'right' }}>
                {t('internal-live-events:odd_cases_column_occurrences')}
              </Box>
              <span />
            </Box>
          )}

          {renderGroups(filteredToReview, toReview, 'review')}

          {filteredToReview.length === 0 && (isFiltering || hasAcceptedTypes) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                px: rowPaddingX,
                pt: '14px',
                pb: acceptedEventCount > 0 ? '14px' : '20px',
                borderTop: (theme) => (hasRows ? 0 : `1px solid ${ruleColor(theme)}`),
                borderBottom: (theme) => `1px solid ${eventRowBorder(theme)}`,
              }}
            >
              {emptyMessage}
            </Typography>
          )}

          {filteredAccepted.length > 0 && (
            <Typography
              sx={{
                px: rowPaddingX,
                pt: '16px',
                pb: '8px',
                fontSize: '10.5px',
                fontWeight: 'bold',
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                borderBottom: (theme) => `1px solid ${ruleColor(theme)}`,
              }}
              color="text.secondary"
            >
              {t('internal-live-events:odd_cases_accepted_section')}
            </Typography>
          )}
          {renderGroups(filteredAccepted, accepted, 'accepted')}

          {acceptedEventCount > 0 && (
            <Typography sx={{ px: rowPaddingX, pt: '14px', pb: '20px', fontSize: '12.5px' }} color="text.secondary">
              {showAccepted
                ? t('internal-live-events:odd_cases_accepted_shown', { count: acceptedEventCount })
                : t('internal-live-events:odd_cases_accepted_hidden', { count: acceptedEventCount })}{' '}
              ·{' '}
              <MuiLink
                component="button"
                type="button"
                onClick={() => setShowAccepted((current) => !current)}
                sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}
              >
                {showAccepted ? t('internal-live-events:odd_cases_hide_accepted') : t('internal-live-events:odd_cases_show_accepted')}
              </MuiLink>
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

type OddCaseGroupRowProps = {
  group: OddCaseTypeGroup
  lastSeenTimestamp?: number
  totalEventCount: number
  isFiltering: boolean
  expanded: boolean
  onToggle: () => void
}

const OddCaseGroupRow: FC<OddCaseGroupRowProps> = ({ group, lastSeenTimestamp, totalEventCount, isFiltering, expanded, onToggle }) => {
  const { t } = useTranslation()
  const typeName = languageServiceNameResolver.eventTypeName(group.typeId) || group.typeId

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onToggle()
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: rowPaddingX,
        py: '11px',
        cursor: 'pointer',
        userSelect: 'none',
        backgroundColor: groupBandBackground,
        borderBottom: (theme) => `1px solid ${ruleColor(theme)}`,
        '&:hover': { backgroundColor: groupBandHoverBackground },
      }}
    >
      <ExpandMore sx={{ fontSize: 16, color: chevronColor, transform: expanded ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms' }} />
      <Typography sx={{ fontSize: '14.5px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeName}</Typography>
      <Box
        component="span"
        sx={{
          px: '7px',
          py: '1px',
          borderRadius: '10px',
          border: (theme) => `1px solid ${ruleColor(theme)}`,
          backgroundColor: 'common.white',
          color: mutedCellText,
          fontSize: '11.5px',
          fontWeight: 'bold',
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {isFiltering ? `${group.events.length} / ${totalEventCount}` : group.events.length}
      </Box>
      {lastSeenTimestamp !== undefined && (
        <Typography
          sx={{ marginLeft: 'auto', paddingLeft: 1, fontSize: '12.5px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          color="text.secondary"
        >
          {t('internal-live-events:odd_cases_last_seen', { date: formatDate(lastSeenTimestamp) })}
        </Typography>
      )}
    </Box>
  )
}

type OddCaseEventRowProps = {
  event: OddCaseEvent
  /** Only the first of a run of events from the same game names it. */
  showGame: boolean
  history?: OddCaseEventHistory
  /** Its history is still being read, as opposed to not being available. */
  isHistoryPending: boolean
}

const OddCaseEventRow: FC<OddCaseEventRowProps> = ({ event, showGame, history, isHistoryPending }) => {
  const { t } = useTranslation()
  const pending = !history && isHistoryPending

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: tableColumns,
        columnGap: tableColumnGap,
        alignItems: 'center',
        px: rowPaddingX,
        py: '9px',
        fontSize: '13.5px',
        borderBottom: (theme) => `1px solid ${eventRowBorder(theme)}`,
        '&:hover': { backgroundColor: eventRowHoverBackground },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        {showGame && (
          <>
            {event.gameIcon && <GameIcon src={event.gameIcon} gameName={event.gameName} size="xtiny" rootStyle={{ flexShrink: 0 }} />}
            <Ellipsis color="text.primary">{event.gameName || event.gameId}</Ellipsis>
          </>
        )}
      </Box>
      <Ellipsis>
        <MuiLink
          component={Link}
          to={`/internal/live-events-tracking/events/${event.gameId}/blueprints/edit/${event.blueprintId}`}
          underline="hover"
          color="primary.main"
          sx={{ fontSize: 'inherit', fontWeight: 'normal' }}
        >
          {event.eventName || event.eventId}
        </MuiLink>
      </Ellipsis>
      <Ellipsis color={mutedCellText}>{event.blueprintName || event.blueprintId}</Ellipsis>
      <Box sx={{ color: mutedCellText, fontVariantNumeric: 'tabular-nums' }}>
        {pending ? <Skeleton variant="text" width={84} height={18} /> : formatDate(history?.lastSeenTimestamp)}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', fontVariantNumeric: 'tabular-nums' }}>
        {pending ? <Skeleton variant="text" width={24} height={18} /> : history?.occurrences ?? '—'}
      </Box>
      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        {/* Same tab: saving in the editor goes back here. */}
        <RowLinkButton
          to={`/internal/live-events-tracking/events/${event.gameId}/event/${event.eventId}/edit`}
          label={t('internal-live-events:odd_cases_open_internal_event_editor')}
          icon={<EditOutlined sx={{ fontSize: 16 }} />}
        />
        <RowLinkButton
          to={customerCalendarUrl(event.gameId)}
          label={t('internal-live-events:odd_cases_open_customer_calendar')}
          icon={<CalendarMonth sx={{ fontSize: 16 }} />}
          newTab
        />
        <RowLinkButton
          to={customerBlueprintUrl({ gameId: event.gameId, eventId: event.eventId, typeId: event.typeId })}
          label={t('internal-live-events:odd_cases_open_customer_blueprint')}
          icon={<OpenInNew sx={{ fontSize: 16 }} />}
          newTab
        />
      </Stack>
    </Box>
  )
}

const Ellipsis: FC<{ children?: ReactNode; color?: string | ((theme: Theme) => string) }> = ({ children, color }) => (
  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color }}>{children}</Box>
)

const RowLinkButton: FC<{ to: string; label: string; icon: ReactNode; newTab?: boolean }> = ({ to, label, icon, newTab }) => (
  <GRTooltip content={label}>
    <IconButton
      component={Link}
      to={to}
      target={newTab ? '_blank' : undefined}
      aria-label={label}
      sx={{
        width: 30,
        height: 30,
        color: quietIconColor,
        '&:hover': { backgroundColor: iconButtonHoverBackground, color: 'primary.main' },
      }}
    >
      {icon}
    </IconButton>
  </GRTooltip>
)

const formatDate = (timestamp?: number) => (timestamp === undefined ? '—' : format(getUTCDate(timestamp), 'MMM dd, yyyy'))
